import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { supabase } from "@/utilities/supabase";

interface CreateBaseApiProps {
  reducerPath: string;
  baseUrl: string;
}

/**
 * Cached access token.
 *
 * `supabase.auth.getSession()` is not a cheap local read: it awaits the
 * client's initializePromise and acquires a global navigator.locks lock. Called
 * from prepareHeaders it runs once per API request, so a page that fires ~20
 * queries on mount serializes all 20 behind that lock before a single fetch
 * leaves the browser — and if the token needs refreshing, 19 of them block on
 * the one doing network I/O.
 *
 * onAuthStateChange fires on SIGNED_IN / TOKEN_REFRESHED / SIGNED_OUT, so this
 * stays current without polling. `expires_at` is checked as a safety net so a
 * missed event can't leave us sending a stale token.
 */
let cachedToken: string | null = null;
let cachedExpiresAt: number | null = null;

/** Refresh slightly early so we never send a token that expires in flight. */
const EXPIRY_SKEW_SECONDS = 60;

supabase.auth.onAuthStateChange((_event, session) => {
  cachedToken = session?.access_token ?? null;
  cachedExpiresAt = session?.expires_at ?? null;
});

const getAccessToken = async (): Promise<string | null> => {
  const nowSeconds = Math.floor(Date.now() / 1000);
  const stillValid =
    cachedToken &&
    cachedExpiresAt &&
    cachedExpiresAt - EXPIRY_SKEW_SECONDS > nowSeconds;

  if (stillValid) return cachedToken;

  const {
    data: { session },
  }: any = await supabase.auth.getSession();

  cachedToken = session?.access_token ?? null;
  cachedExpiresAt = session?.expires_at ?? null;
  return cachedToken;
};

export const createBaseApiWithAuth = ({
  reducerPath,
  baseUrl,
}: CreateBaseApiProps) => {
  return {
    reducerPath,
    baseQuery: fetchBaseQuery({
      baseUrl,
      prepareHeaders: async (headers, { extra }: any) => {
        const meta: any = extra?.meta;

        /*
          Check if the request should skip authorization.
          Even though this middleware is used everywhere, this is JUST for 
          docs/signatures (for anonymous signing rn). If we start adding to 
          this, we should consider breaking out different baseApis 
        */
        const isAuthSkipped = meta?.personId || meta?.invitationId;

        if (!isAuthSkipped) {
          const accessToken = await getAccessToken();

          if (accessToken) {
            headers.set("authorization", `Bearer ${accessToken}`);
          }
        }

        return headers;
      },
    }),
  };
};
